import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Add } from '@mui/icons-material';
import { State } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { AddressType } from '@uplift/types';

import {
  AddressSearchInput, AddressVerifyModal, useAddressVerificationContext
} from '../../../../../AddressVerification';
import { Button } from '../../../../../Button';
import { MaskedInput, StatesAutocomplete, TextInput } from '../../../../../Inputs';
import { SettingsAddressFormType } from './types';

export type SettingsAddressFormProps = {
  states: State[];
  getFieldName?: (fieldName: keyof SettingsAddressFormType) => string;
};

const useStyles = makeStyles()((theme) => ({
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  maxWidth400: {
    maxWidth: '400px',
  },
  maxWidth248: {
    maxWidth: '248px',
  },
  maxWidth112: {
    maxWidth: '112px'
  },
  addButton: {
    '& > button': {
      textTransform: 'unset',
      textAlign: 'left',
      padding: '12px 6px 12px 6px'
    }
  },
}));

const getAllFieldNames = (getFieldName: SettingsAddressFormProps['getFieldName']) => {
  const fieldKeyToName = {
    address1: 'address1',
    address2: 'address2',
    city: 'city',
    state: 'state',
    zip: 'zip'
  };

  if (getFieldName) {
    Object.keys(fieldKeyToName).forEach((key) => {
      const keyName = key as keyof SettingsAddressFormType;

      fieldKeyToName[keyName] = getFieldName(keyName);
    });
  }

  return fieldKeyToName;
};

export const SettingsAddressForm: React.VFC<SettingsAddressFormProps> = ({ states, getFieldName }) => {
  const fieldNames = useMemo(() => getAllFieldNames(getFieldName), [ getFieldName ]);
  const { state: { shouldReverifyAddress }, dispatch } = useAddressVerificationContext();
  const { castAddressToAddressWithStateAsId, castAddressWithStateAsFullOrShortName } = useCastAddress(states);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { classes } = useStyles();
  const formik = useFormikContext();

  const values = {
    address1: formik.getFieldMeta(fieldNames.address1).value ?? '',
    address2: formik.getFieldMeta(fieldNames.address2).value ?? '',
    city: formik.getFieldMeta(fieldNames.city).value ?? '',
    state: formik.getFieldMeta(fieldNames.state).value ?? '',
    zip: formik.getFieldMeta(fieldNames.zip).value ?? '',
  } as AddressType;

  const [ showAddress2, setShowAddress2 ] = useState(!!values.address2);
  const l10n = useL10n();

  useEffect(() => setShowAddress2(Boolean(values.address2)), [ values.address2 ]);

  const { setFieldTouched, setFieldValue } = formik;
  const setAddressFields = useCallback((address: AddressType) => {
    Object.keys(address).forEach((key) => {
      const fieldKey = key as keyof AddressType;

      setFieldValue(fieldNames[fieldKey], address[fieldKey]);
    });

    // set all address fields as touched after updating values - https://github.com/jaredpalmer/formik/issues/2059#issuecomment-612733378
    setTimeout(() => Object.keys(address).forEach((key) => {
      const fieldKey = key as keyof AddressType;

      setFieldTouched(fieldNames[fieldKey]);
    }));
  }, [ setFieldValue, fieldNames, setFieldTouched ]);

  const addressSelectHandler = useCallback((address: AddressType) => {
    const castedAddress = castAddressToAddressWithStateAsId(address);

    dispatch({
      type: 'setParsedVerifiedAddress',
      payload: {
        parsedVerifiedAddress: castedAddress
      }
    });

    setAddressFields(castedAddress);
  }, [ dispatch, castAddressToAddressWithStateAsId, setAddressFields ]);

  const closeVerifyModalHandler = useCallback(() => {
    dispatch({
      type: 'setShouldReverifyAddress',
      payload: {
        shouldReverifyAddress: false
      }
    });
  }, [ dispatch ]);

  const { submitForm } = formik;
  const confirmAddressHandler = useCallback((address: AddressType) => {
    closeVerifyModalHandler();
    addressSelectHandler(address);
    submitForm();
  }, [ submitForm, closeVerifyModalHandler, addressSelectHandler ]);


  const renderReverify = () => {
    const addressVerify = castAddressWithStateAsFullOrShortName(values, 'shortName');

    return (
      <AddressVerifyModal
        addressVerify={addressVerify}
        open
        onClose={closeVerifyModalHandler}
        onAddressConfirm={confirmAddressHandler}
      />
    );
  };

  return (
    <>
      {shouldReverifyAddress && renderReverify()}
      <div className={classes.formGroup}>
        {/* since form is reinitialized, we
              check if address1 is not undefined before
              rendering address search input */}
        {values.address1 !== undefined && (
          <AddressSearchInput
            name={fieldNames.address1}
            label={l10n.matching.insurance.streetAddress}
            onAddressSelect={addressSelectHandler}
            inputClasses={{ root: classes.maxWidth400 }}
            maxWidth="400px"
            width="100%"
          />
        )}
        {!showAddress2 && (
          <div className={classes.addButton}>
            <Button
              size="large"
              color="secondary"
              startIcon={<Add color="inherit" />}
              onClick={() => setShowAddress2(true)}
            >
              {!isSmall && l10n.matching.insurance.addStreetAddress2}
              {isSmall && l10n.matching.insurance.addStreetAddress2SmallBreakpoint}
            </Button>
          </div>
        )}
        {showAddress2 && (
          <TextInput
            fullWidth
            id="address2"
            name={fieldNames.address2}
            label={l10n.matching.insurance.streetAddress2}
            inputClasses={{ root: classes.maxWidth400 }}
          />
        )}
        <TextInput
          fullWidth
          id="city"
          name={fieldNames.city}
          label={l10n.editGeneralInfo.city}
          inputClasses={{ root: classes.maxWidth400 }}
        />
        <StatesAutocomplete
          name={fieldNames.state}
          label={l10n.editGeneralInfo.state}
          inputClasses={{ root: classes.maxWidth248 }}
        />
        <MaskedInput
          id="zip"
          name={fieldNames.zip}
          label={l10n.editGeneralInfo.zipCode}
          type="zip"
          inputProps={{ inputMode: 'numeric', }}
          showPlaceholder={false}
          inputClasses={{ root: classes.maxWidth112 }}
        />
      </div>
    </>
  );
};

type StateProperty = keyof Pick<State, 'fullName' | 'shortName' | 'id'>;

// this hook is used to take an object that contains AddressType properties
// with the state being in the form of fullName or shortName, and converts it
// to an object of required shape AddressType with state as the state ID
export const useCastAddress = (allStates: State[]) => {

  const findStateByFullOrShortName = useCallback((state: string, stateProperty: StateProperty) => {
    return allStates.find(({ fullName, shortName }) => [ fullName, shortName ].includes(state))?.[stateProperty];
  }, [ allStates ]);

  const findStateById = useCallback((stateId: string, stateProperty: StateProperty) => {
    return allStates.find(({ id }) => id === stateId)?.[stateProperty];
  }, [ allStates ]);

  const castAddressToAddressWithStateAsId = useCallback(<T extends AddressType>(address: T) => {
    const castedAddress: AddressType = {
      address1: address.address1,
      address2: address.address2 ?? '',
      city: address.city,
      state: findStateByFullOrShortName(address.state, 'id') ?? '',
      zip: address.zip
    };

    return castedAddress;
  }, [ findStateByFullOrShortName ]);

  const castAddressWithStateAsFullOrShortName = useCallback(<T extends AddressType>(address: T, stateProperty: StateProperty) => {
    const castedAddress: AddressType = {
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: findStateById(address.state, stateProperty) ?? '',
      zip: address.zip,
    };

    return castedAddress;
  }, [ findStateById ]);

  return { castAddressToAddressWithStateAsId, castAddressWithStateAsFullOrShortName };
};
